import { Utils } from '@pacem/pacem-core';
import { LABEL_PREFIX, GPUTextureUsage, GPUBufferUsage, GPUMapMode } from './constants';
import { RenderPipelines } from './pipelinefactory';
//namespace Pacem.Drawing3D.WebGPU {
export class Raycasters {
    // learn from:
    // https://webglfundamentals.org/webgl/lessons/webgl-qna-how-to-get-the-3d-coordinates-of-a-mouse-click.html
    // https://github.com/mrdoob/three.js/blob/master/src/core/Raycaster.js
    // https://shi-yan.github.io/webgpuunleashed/Control/object_picking.html
    // https://gist.github.com/greggman/57c3c0d3cf1db14f1baad2d9b0094397 (color picking)
    static { this._memoizer = new WeakMap(); }
    static dispose(ctx) {
        const memoizer = this._memoizer;
        if (memoizer.has(ctx)) {
            const cache = memoizer.get(ctx);
            for (let raycaster of cache.values()) {
                raycaster.destroy();
            }
            cache.clear();
            memoizer.delete(ctx);
        }
    }
    /** Creates a raycaster that uses color picking as a strategy to hit-test objects. */
    static createColorPicking(ctx) {
        const memoizer = this._memoizer;
        if (!memoizer.has(ctx)) {
            memoizer.set(ctx, new Map());
        }
        const cache = memoizer.get(ctx);
        const key = 'colorpick';
        if (cache.has(key)) {
            return cache.get(key);
        }
        const retval = new ColorPickingRaycaster(ctx);
        cache.set(key, retval);
        return retval;
    }
}
const NUM_PIXELS = 1;
class ColorPickingRaycaster {
    constructor(_context) {
        this._context = _context;
        this._objects = new Map();
        this.frame = (interaction, ...buffers) => {
            if (Utils.isNull(this._encoder)) {
                throw new Error('Null encoder. Maybe you missed to call the \'begin\' method first.');
            }
            const objects = Array.from(buffers);
            const pipeline = RenderPipelines.createColorPicking(this._context, objects);
            pipeline?.frame(this._pass, interaction, ...buffers);
            for (let buffer of objects) {
                const item = buffer.item;
                if (item.inert) {
                    continue;
                }
                //buffer.lock();
                this._objects.set(buffer.id, buffer);
            }
        };
        this.end = (pixel) => {
            if (!Utils.isNull(this._handle)) {
                return this._handle;
            }
            const pass = this._pass;
            const commandEncoder = this._encoder;
            const { device } = this._context;
            const { render } = this._textures;
            const pickTexture = render.texture;
            const pickBuffer = this._ensureBuffer();
            pass.end();
            commandEncoder.copyTextureToBuffer({
                texture: pickTexture,
                // mipLevel: 0,
                origin: {
                    x: pixel.x,
                    y: pixel.y,
                }
            }, {
                buffer: pickBuffer,
                bytesPerRow: ((NUM_PIXELS * 4 + 255) | 0) * 256,
                rowsPerImage: 1,
            }, {
                width: NUM_PIXELS,
                // height: 1,
                // depth: 1,
            });
            device.queue.submit([commandEncoder.finish()]);
            // remove
            this._encoder = null;
            const _this = this;
            const objects = _this._objects;
            const resetHandle = () => {
                _this._handle = null;
            };
            return this._handle = new Promise((resolve, _) => {
                pickBuffer.mapAsync(GPUMapMode.READ, 0, 4 * NUM_PIXELS).then(_ => {
                    const ids = new Uint32Array(pickBuffer.getMappedRange(0, 4 * NUM_PIXELS));
                    const id = ids[0];
                    pickBuffer.unmap();
                    // remove promise reference
                    resetHandle();
                    const buffer = objects.get(id);
                    if (!buffer) {
                        resolve([]);
                    }
                    else {
                        resolve([buffer.item]);
                    }
                    // cleanup
                    //pickBuffer.destroy();
                    //pickTexture.destroy();
                    //objects.values().forEach(b => b.unlock());
                });
            });
        };
    }
    _createTexture(format) {
        const { device, canvas } = this._context;
        const width = Math.min(device.limits.maxTextureDimension2D, canvas.clientWidth), height = Math.min(device.limits.maxTextureDimension2D, canvas.clientHeight);
        return device.createTexture({
            size: [width, height, 1],
            format,
            usage: GPUTextureUsage.COPY_SRC | GPUTextureUsage.RENDER_ATTACHMENT
        });
    }
    _ensureBuffer() {
        const buffer = this._buffer;
        if (!Utils.isNull(buffer)) {
            return buffer;
        }
        const { device } = this._context;
        return this._buffer = device.createBuffer({
            label: LABEL_PREFIX + 'colorpick-output-buffer',
            size: NUM_PIXELS * 4,
            usage: GPUBufferUsage.MAP_READ | GPUBufferUsage.COPY_DST,
        });
    }
    _ensureTextures() {
        const textures = this._textures;
        if (!Utils.isNull(textures)) {
            return textures;
        }
        const renderTex = this._createTexture('r32uint'), depthTex = this._createTexture('depth24plus');
        const render = { texture: renderTex, view: renderTex.createView() };
        const depth = { texture: depthTex, view: depthTex.createView() };
        return this._textures = { render, depth };
    }
    ;
    _destroyTexture(texture) {
        if (Utils.isNull(texture)) {
            return;
        }
        texture.texture?.destroy();
    }
    destroy() {
        const textures = this._textures, buffer = this._buffer;
        if (!Utils.isNullOrEmpty(textures)) {
            const { render, depth } = textures;
            this._destroyTexture(render);
            this._destroyTexture(depth);
            this._textures = null;
        }
        if (!Utils.isNull(buffer)) {
            buffer.destroy();
            this._buffer = null;
        }
    }
    begin() {
        const { device } = this._context;
        this._encoder = device.createCommandEncoder({ label: LABEL_PREFIX + 'colorpick-raycaster' });
        const { render, depth } = this._ensureTextures();
        const passDescriptor = {
            label: LABEL_PREFIX + 'colorpick-raycaster-pass',
            colorAttachments: [
                {
                    view: render.view,
                    clearValue: { r: 0, g: 0, b: 0, a: 0 },
                    loadOp: 'clear',
                    storeOp: 'store',
                },
            ],
            depthStencilAttachment: {
                view: depth.view, // Assigned later
                depthClearValue: 1,
                depthLoadOp: 'clear',
                depthStoreOp: 'store',
            },
        };
        this._pass = this._encoder.beginRenderPass(passDescriptor);
        this._objects.clear();
    }
}
